import { createContext, type PropsWithChildren, useCallback, useContext, useState } from 'react'
import { useWorkOrder } from '@/api/hooks/useWorkOrder'
import RepairPartsActionDialog from '@/components/organisms/repair-parts-action-dialog'
import type { RepairAction } from '@/lib/repair-parts'

type RepairPartsActionContextValue = {
  openRepairAction(workOrderId: string, action?: RepairAction): void
}

const RepairPartsActionContext = createContext<RepairPartsActionContextValue | undefined>(undefined)

/**
 * Hosts a single repair-parts action dialog above the kanban cards. Opening it fetches the full
 * work order by id (the list item that a card carries doesn't include `repairParts`), and because
 * the dialog lives here — not inside the card — it survives the card unmounting when a send
 * re-buckets it into the paint column.
 */
export const RepairPartsActionProvider = ({ children }: PropsWithChildren) => {
  const [workOrderId, setWorkOrderId] = useState<string | null>(null)
  const [initialAction, setInitialAction] = useState<RepairAction | undefined>(undefined)
  const [open, setOpen] = useState(false)

  const openRepairAction = useCallback((id: string, action?: RepairAction) => {
    setWorkOrderId(id)
    setInitialAction(action)
    setOpen(true)
  }, [])

  const { data: workOrder } = useWorkOrder(workOrderId ?? '', { enabled: !!workOrderId })

  return (
    <RepairPartsActionContext.Provider value={{ openRepairAction }}>
      {children}
      {workOrder && (
        <RepairPartsActionDialog
          key={workOrder.id}
          workOrder={workOrder}
          open={open}
          onOpenChange={setOpen}
          initialAction={initialAction}
        />
      )}
    </RepairPartsActionContext.Provider>
  )
}

export const useRepairPartsAction = () => {
  const context = useContext(RepairPartsActionContext)
  if (!context) {
    throw new Error('useRepairPartsAction must be used within a RepairPartsActionProvider')
  }
  return context
}
